/**
 * Composite mock transport — ONE Transport implementing every merged contract
 * the Admin Panel consumes (identity + geo + analytics + the admin user
 * directory + merchants + orders + drivers + dispatch + notifications +
 * money + theme), active only while VITE_API_BASE_URL is unset (§16.2). Domain
 * handlers are DYNAMICALLY imported on first touch so their fixtures never
 * weigh down the initial chunk (§11.2) — route chunks stay lean whether
 * mocked or live.
 */

import type { Transport, TransportRequest, TransportResponse } from "../transport";
import {
  createMockIdentityTransport,
  type MockIdentityOptions,
} from "../../auth/mock/mockIdentity";
import { isAuthenticated, localeOf, missingIdempotencyKey, mockError } from "./mockShared";

type DomainHandler = (request: TransportRequest) => TransportResponse | null;

export type MockTransportOptions = MockIdentityOptions;

export function createMockTransport(options: MockTransportOptions = {}): Transport {
  const identity = createMockIdentityTransport(options);
  const latency = options.latencyMs ?? 350;
  let domainHandlers: Promise<ReadonlyArray<DomainHandler>> | null = null;

  const loadDomains = (): Promise<ReadonlyArray<DomainHandler>> => {
    domainHandlers ??= Promise.all([
      import("./mockGeo"),
      import("./mockAnalytics"),
      import("./mockUsers"),
      import("./mockRoles"),
      import("./mockMerchants"),
      import("./mockOrders"),
      import("./mockDrivers"),
      import("./mockCustomers"),
      import("./mockTracking"),
      import("./mockDispatch"),
      import("./mockAudit"),
      import("./mockNotifications"),
      import("./mockWallets"),
      import("./mockSettlement"),
      import("./mockSubscriptions"),
      import("./mockPromotions"),
      import("./mockSettings"),
      import("./mockPayments"),
      import("./mockTheme"),
      import("./mockMarketing"),
      import("./mockLegal"),
    ]).then(([geo, analytics, users, roles, merchants, orders, drivers, customers, tracking, dispatch, audit, notifications, wallets, settlement, subscriptions, promotions, settings, payments, theme, marketing, legal]) => [
      geo.handleGeoRequest,
      analytics.handleAnalyticsRequest,
      users.handleUsersRequest,
      roles.handleRolesRequest,
      merchants.handleMerchantsRequest,
      orders.handleOrdersRequest,
      drivers.handleDriversRequest,
      drivers.handleVehicleTypesRequest,
      customers.handleCustomersRequest,
      tracking.handleTrackingRequest,
      dispatch.handleDispatchRequest,
      audit.handleAuditRequest,
      notifications.handleNotificationsRequest,
      wallets.handleWalletsRequest,
      settlement.handleSettlementRequest,
      subscriptions.handleSubscriptionsRequest,
      promotions.handlePromotionsRequest,
      settings.handleSettingsRequest,
      payments.handlePaymentsRequest,
      theme.handleThemeRequest,
      marketing.handleMarketingRequest,
      legal.handleLegalRequest,
    ]);
    return domainHandlers;
  };

  const isDomainPath = (path: string): boolean =>
    path.startsWith("/admin/zones") ||
    path.startsWith("/admin/analytics") ||
    path.startsWith("/admin/users") ||
    path.startsWith("/admin/roles") ||
    path.startsWith("/admin/permissions") ||
    path.startsWith("/geo/") ||
    path.startsWith("/admin/merchants") ||
    path.startsWith("/admin/merchant-applications") ||
    path.startsWith("/admin/merchant-categories") ||
    path.startsWith("/admin/engagement") ||
    path.startsWith("/merchant-categories") ||
    path.startsWith("/admin/orders") ||
    path.startsWith("/orders/") ||
    path.startsWith("/order-categories") ||
    path.startsWith("/admin/drivers") ||
    path.startsWith("/admin/vehicle-types") ||
    path.startsWith("/vehicle-types") ||
    path.startsWith("/admin/customers") ||
    path.startsWith("/admin/tracking") ||
    path.startsWith("/admin/dispatch") ||
    path.startsWith("/admin/audit") ||
    path.startsWith("/admin/geofence") ||
    path.startsWith("/admin/notification-templates") ||
    path.startsWith("/admin/notifications") ||
    path.startsWith("/wallets") ||
    path.startsWith("/settlement/") ||
    path.startsWith("/subscriptions") ||
    path.startsWith("/admin/promotions") ||
    path.startsWith("/admin/promotion-applications") ||
    path.startsWith("/admin/marketing") ||
    // M7-309 — the privacy policy (admin legal surface).
    path.startsWith("/admin/legal") ||
    path.startsWith("/promotions/") ||
    path.startsWith("/admin/settings") ||
    path.startsWith("/admin/flags") ||
    path.startsWith("/config/flags") ||
    path.startsWith("/payments/providers") ||
    path === "/theme" ||
    path.startsWith("/theme?");

  // Public reads (security: []): the category directory (merchants.yaml
  // v1.1.0), the vehicle-type list (drivers.yaml v1.5.0), GET /theme (theme.yaml — clients theme themselves pre-auth) and
  // GET /config/flags (settings.yaml — a client must be able to decide which
  // surfaces exist before it knows who is looking).
  const isPublicRequest = (request: TransportRequest): boolean =>
    request.path.startsWith("/merchant-categories") ||
    // drivers.yaml v1.5.0: the vehicle-type vocabulary is a public read, like
    // the category directory — the merchant and driver apps need names before
    // anyone signs in.
    (request.method === "GET" && request.path.startsWith("/vehicle-types")) ||
    (request.method === "GET" && request.path.startsWith("/config/flags")) ||
    (request.method === "GET" && (request.path === "/theme" || request.path.startsWith("/theme?")));

  return async (request) => {
    if (!isDomainPath(request.path)) return identity(request);

    if (latency > 0) await new Promise((resolve) => setTimeout(resolve, latency));
    const locale = localeOf(request);
    if (!isPublicRequest(request) && !isAuthenticated(request)) {
      return mockError(401, "AUTH_TOKEN_INVALID", locale);
    }
    if (missingIdempotencyKey(request)) return mockError(400, "IDEMPOTENCY_KEY_MISSING", locale);

    const handlers = await loadDomains();
    for (const handler of handlers) {
      const response = handler(request);
      if (response !== null) return response;
    }
    return mockError(404, "RESOURCE_NOT_FOUND", locale);
  };
}
